import React, { FC, useContext, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { sanitizeUrl } from "@braintree/sanitize-url";
import { AppContext } from "context/app";
import {
  LoginForm as PepLoginForm,
  LoginFormValues,
} from "@pepdirect/v3/ui/LoginForm";
import { Button } from "@pepdirect/v3/ui/Button";
// UPDATE TO V3 SCHEMA ONCE THE PAGE IS UPDATED
import {
  BrandConfigCustomizationsImage,
  useLoginUserMutation,
} from "services/graphql/generated";
import { logUserChange } from "services/analytics";
import { getLinkFromTenant } from "helpers/tenant";
import { getRedirectUrl } from "helpers/routing";
import { PageRoutes } from "constants/pageRoutes";
import { StyledForm, StyledHeading, StyledSignup } from "./index.styled";

export const LoginForm: FC = () => {
  const { tenant } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loginMutation, { error }] = useLoginUserMutation();
  const router = useRouter();

  const canSignUp = tenant?.capabilities.signUp.enabled;
  const { successRedirectTo, successRedirectSite } = router.query;

  const privacyLink = getLinkFromTenant(tenant, "privacy");
  const termsLink = getLinkFromTenant(tenant, "terms");

  const accountIconUrl = tenant?.brandConfig?.customizations?.images?.find(
    (img: BrandConfigCustomizationsImage) => img.name === "accountIconUrl"
  );

  const signinPageTitle =
    tenant?.brandConfig?.customizations?.account?.signinPageTitle ||
    "Log in to your account";

  const mappedErrorMessage = useMemo(() => {
    switch (error?.message) {
      case "not_found":
      case "invalid_password":
        return "Oops, that email and password combination isn't correct. Please check and try again or try resetting your password.";
      default:
        return "An error has occurred. Please try again later.";
    }
  }, [error]);

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    setIsLoading(true);
    try {
      const { data } = await loginMutation({
        variables: { email, password },
      });
      logUserChange(data?.loginUser?.id);
      const redirectUrl = getRedirectUrl(
        successRedirectTo && sanitizeUrl(successRedirectTo as string),
        successRedirectSite && sanitizeUrl(successRedirectSite as string),
        tenant?.fallbackUrl
      );
      router.push(redirectUrl || "/dashboard/account");
    } catch {
      setIsLoading(false);
    }
  };

  const goToForgotPassword = () => {
    router.push({
      pathname: "/forgot-password",
      query: {
        ...(successRedirectTo && { successRedirectTo }),
        ...(successRedirectSite && { successRedirectSite }),
      },
    });
  };

  const goToCreateAccount = () => {
    const { successRedirectTo, successRedirectSite } = router.query;
    router.push({
      pathname: PageRoutes.signUp,
      query: {
        ...(successRedirectTo && { successRedirectTo }),
        ...(successRedirectSite && { successRedirectSite }),
      },
    });
  };

  return (
    <StyledForm>
      <PepLoginForm
        error={error ? mappedErrorMessage : ""}
        termsUrl={termsLink?.url}
        privacyUrl={privacyLink?.url}
        fallbackUrl={tenant?.fallbackUrl || ""}
        goToForgotPassword={goToForgotPassword}
        isLoading={isLoading}
        onSubmit={onSubmit}
        accountLogo={accountIconUrl}
        signinPageTitle={signinPageTitle}
      >
        {canSignUp && (
          <StyledSignup>
            <StyledHeading level="h3" size="m">
              New{" "}
              {`${
                tenant?.brandConfig.title
                  ? "to " + tenant?.brandConfig.title + "?"
                  : "here?"
              }`}
            </StyledHeading>
            <Button
              type="tertiary"
              fullWidth
              onClick={goToCreateAccount}
              disabled={isLoading}
            >
              Create an account
            </Button>
          </StyledSignup>
        )}
      </PepLoginForm>
    </StyledForm>
  );
};
